import { FunctionComponent } from "react";
import { connect } from "react-redux";
import Loading from "../../Loading/Loading";
import { themeColors } from "../../theme";
import "../../../theme-vars.css";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";
import { FormControl, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";


/* ------------------------------------------------------------------
   TYPES
-------------------------------------------------------------------*/
type props = {
  open: boolean;
  close: () => void;
  organizationId: any;
  organizationUsers: any;
  edit?: boolean;
  editData?: any;
  editClose?: () => void;
  leadSourceList?: any;
  apiData?: any;
  theme?: any;
  allIndustries: { industry_id: string; status: string; industry_name: string; screens: string[] }[];
  selectedIndustries: string;
  handleIndustriesChange: (e: any) => void;
  onSave: () => void;
  load: boolean;
};

/* ------------------------------------------------------------------
   STYLED MUI FORM CONTROL
-------------------------------------------------------------------*/
const primaryColor = "#292744";

const CustomFormControl = styled(FormControl)({
  minWidth: "100%",
  "& .MuiInputLabel-root": {
    color: primaryColor,
    fontWeight: 500,
    fontSize: 15,
    background: "#f7f8fa",
    padding: "0 4px",
  },
  "& .MuiInputLabel-root.Mui-required .MuiInputLabel-asterisk": {
    color: "#d32f2f",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    "& fieldset": {
      borderColor: "#bfc7d5",
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
  },
  "& .MuiSelect-select": {
    padding: "14px",
    fontSize: 'var(--font-size-medium)',
    fontFamily: 'var(--font-family-main)',
    color: "#222",
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
  },
});


/* ------------------------------------------------------------------
   MAIN COMPONENT
   Displays modal to select industry before booking.
-------------------------------------------------------------------*/
const AddBookingModel: FunctionComponent<props> = ({
  organizationId,
  open,
  close,
  edit,
  editData,
  editClose,
  leadSourceList,
  apiData,
  theme,
  allIndustries,
  selectedIndustries,
  handleIndustriesChange,
  onSave,
  load
}) => {
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  /* ---------------------------------------------------------------
   MODAL STYLES
   Customizes react-modal to match dark/light theme + responsiveness
----------------------------------------------------------------*/
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      maxWidth: 380,
      minWidth: 320,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      style={modalStyle}
      ariaHideApp={false}
    >
      {load && <Loading />}
      <Box
        sx={{
          width: "100%",
          maxWidth: 340,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <form style={{ width: "100%" }}>
          {/* ------------------------------ */}
          {/* MODAL TITLE */}
          {/* ------------------------------ */}
          <Box sx={{ mb: 2 }}>
            <h3
              style={{
                color: theme ? "#272944" : "#fff",
                fontFamily: "var(--font-family-main)",
                fontSize: "var(--font-size-large)",
                marginBottom: 24,
                textAlign: "center",
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              Select Industries
            </h3>
          </Box>
          {/* ------------------------------ */}
          {/* INDUSTRY DROPDOWN */}
          {/* ------------------------------ */}
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12}>
              <CustomFormControl variant="outlined" required={true}>
                <InputLabel
                  style={{ color: '#555', fontFamily: 'var(--font-family-main)', fontSize: 'var(--font-size-medium)' }}
                  required={true}
                >
                  Select an Industries
                </InputLabel>
                <Select
                  label="Select an Industries"
                  value={selectedIndustries}
                  onChange={handleIndustriesChange}
                >
                  <MenuItem value="">Select</MenuItem>
                  {allIndustries.map((option, index) => (
                    <MenuItem
                      key={index}
                      value={option.industry_id}
                      style={{
                        fontFamily: "var(--font-family-main)",
                        fontSize: "var(--font-size-medium)",
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 265,
                        }}
                      >
                        {option.industry_name}
                      </span>
                    </MenuItem>
                  ))}
                </Select>
              </CustomFormControl>
            </Grid>
          </Grid>

          {/* ------------------------------ */}
          {/* ACTION BUTTONS */}
          {/* ------------------------------ */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
                width: "100%",
              }}
            >
              <Box style={{ marginRight: 8 }}>
                <StyledCancelButton onClick={close} isMobile={isMobile}>
                  Cancel
                </StyledCancelButton>
              </Box>
              <Box>
                <StyledSubmitButton
                  onClick={onSave}
                  themeMode={themeMUI.palette.mode}
                  isMobile={isMobile}
                >
                  {load ? (
                    <span style={{ display: "flex", justifyContent: "center" }}>
                      <Circles color="#fff" height={20} width={20} />
                    </span>
                  ) : (
                    <>Submit</>
                  )}
                </StyledSubmitButton>
              </Box>
            </Box>
          </Grid>
        </form>
      </Box>
    </Modal >
  );
};

/* ------------------------------------------------------------------
   REDUX CONNECTION
-------------------------------------------------------------------*/
const mapStateToProps = (state: any) => {
  return {
    organizationId: state.organization.id,
    organizationUsers: state.organizationUsers.data,
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(AddBookingModel);
